import React, { forwardRef, memo, useMemo, type MouseEvent } from 'react';

import classnames from 'classnames';

import { omit } from '../../../../utils';
import { pickAttrs } from '../../../../utils/pickAttrs';
import { Checkbox } from '../../../Checkbox/Checkbox';
import { Icon } from '../../../Icon';
import { Tooltip } from '../../../Tooltip';
import { Text } from '../../../Typography';
import { isOptionGroup } from '../../Select.helpers';

import styles from './SelectOption.module.scss';

import type { InternalSelectOption, SelectCheckmarkAlign, SelectMode } from '../../Select.types';

export type SelectOptionProps = Omit<React.ComponentPropsWithoutRef<'div'>, 'onSelect'> & {
    active: boolean;
    selected: boolean;
    option: InternalSelectOption;
    checkmarkAlign?: SelectCheckmarkAlign;
    hideCheckmark?: boolean;
    renderOptionContent?: (option: InternalSelectOption) => React.ReactNode;
    virtual?: boolean;
    mode?: SelectMode;
    onSelect?: (option: InternalSelectOption) => void;
    getItemProps: (props: Record<string, any>) => Record<string, any>;
};

const Option = forwardRef<HTMLDivElement, SelectOptionProps>(
    (
        {
            active,
            selected,
            option,
            renderOptionContent,
            checkmarkAlign,
            hideCheckmark,
            virtual,
            mode,
            onSelect,
            getItemProps,
            ...rest
        }: SelectOptionProps,
        ref
    ) => {
        const optionHtmlAttrs = useMemo(
            () => omit(pickAttrs(option), ['type', 'label', 'id', 'value']) as Record<string, any>,
            [option]
        );

        const memoizedCheckmark = useMemo(() => {
            if (mode === 'single' && selected) {
                return <Icon name="s-icon-check" className={styles.checkmark} />;
            }
            if (mode === 'multiple') {
                const checkbox = (
                    <Checkbox
                        className={styles.checkbox}
                        checked={selected}
                        disabled={option.disabled}
                        tabIndex={-1}
                        aria-label="Select option checkbox"
                    />
                );
                if (option.tooltip) {
                    return (
                        <Tooltip
                            align={{ offset: [-4, 0] }}
                            disabled={option.disabled}
                            title={option.tooltip}
                        >
                            {checkbox}
                        </Tooltip>
                    );
                }
                return checkbox;
            }
            return null;
        }, [mode, selected, option.disabled, option.tooltip]);

        const shouldHideCheckmark = hideCheckmark || option.hideCheckmark;

        const itemProps = useMemo(
            () =>
                getItemProps({
                    ...optionHtmlAttrs,
                    onClick: (e: MouseEvent) => {
                        optionHtmlAttrs.onClick?.(e);
                        if (!isOptionGroup(option)) {
                            return onSelect?.(option);
                        }
                        return undefined;
                    },
                    className: classnames(
                        styles.optionWrapper,
                        {
                            [styles.active]: active,
                            [styles.selected]: selected,
                            [styles.optionGroupItem]: isOptionGroup(option),
                            [styles.virtual]: virtual,
                            [styles.multiple]: mode === 'multiple'
                        },
                        optionHtmlAttrs.className,
                        rest.className
                    ),
                    'aria-label':
                        typeof option['aria-label'] === 'string' ? option['aria-label'] : undefined
                }),
            [
                option,
                active,
                selected,
                virtual,
                mode,
                optionHtmlAttrs,
                getItemProps,
                onSelect,
                rest.className
            ]
        );

        return (
            <div ref={ref} {...rest} {...itemProps}>
                {isOptionGroup(option) ? (
                    <Text type="h4" title={option.label}>
                        {option.label}
                    </Text>
                ) : (
                    <div
                        className={classnames(styles.option, {
                            [styles.active]: active,
                            [styles.selected]: selected,
                            [styles.disabled]: option.disabled,
                            [styles.checkmarkAlignStart]: checkmarkAlign === 'start',
                            [styles.checkmarkAlignCenter]: checkmarkAlign === 'center'
                        })}
                    >
                        {!shouldHideCheckmark && (
                            <div className={styles.checkmarkContainer}>{memoizedCheckmark}</div>
                        )}
                        {renderOptionContent ? (
                            renderOptionContent(option)
                        ) : (
                            <span className={styles.optionContent}>{option.label}</span>
                        )}
                    </div>
                )}
            </div>
        );
    }
);

Option.displayName = 'SelectOption';

export const SelectOption = memo(Option);
